/**
 * Auth Store (Zustand)
 *
 * Centralized authentication state management with localStorage persistence.
 * Manages user authentication state, session tokens, and auth-related operations.
 *
 * @module features/auth/store/authStore
 * @see SPEC-005.5-auth-login-ui Requirements 3.1-3.7, 4.1-4.6
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ApiError } from '@linkode/shared';

// ============================================================================
// Constants
// ============================================================================

/** localStorage key for persisted auth state */
const STORAGE_KEY = 'linkode-auth';

/** API base URL from environment */
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:8787' : 'https://api-staging.getlinkode.com');

// ============================================================================
// State Interface
// ============================================================================

/**
 * Authentication state interface
 *
 * @see Requirements 3.2
 */
export interface AuthState {
  // ─────────────────────────────────────────────────────────────────────────
  // State Fields
  // ─────────────────────────────────────────────────────────────────────────

  /** Whether user is currently authenticated */
  isAuthenticated: boolean;

  /** User ID from session (null if not authenticated) */
  userId: string | null;

  /** User email from session (null if not authenticated) */
  email: string | null;

  /** Bearer token for API requests (null if not authenticated) */
  sessionToken: string | null;

  /** Token expiration timestamp in milliseconds (null if not authenticated) */
  expiresAt: number | null;

  /** Loading state for async operations */
  isLoading: boolean;

  /** Last error from auth operations (null if no error) */
  error: ApiError | null;

  /**
   * Whether Zustand persist has completed hydration from localStorage.
   * CRITICAL: Must wait for this to be true before accessing sessionToken.
   * @see https://github.com/pmndrs/zustand/blob/main/docs/integrations/persisting-store-data.md#hydration-and-asynchronous-storages
   */
  _hasHydrated: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Login action - stores session token and marks user as authenticated
   *
   * @param sessionToken - Bearer token from auth API
   * @param expiresAt - Token expiration timestamp (ms)
   * @param userId - Optional user ID
   * @param email - Optional user email
   * @see Requirements 3.4, 4.1, 4.2
   */
  login: (sessionToken: string, expiresAt: number, userId?: string, email?: string) => void;

  /**
   * Logout action - clears all auth data and calls logout API
   *
   * @see Requirements 3.5, 4.6, 10.3
   */
  logout: () => Promise<void>;

  /**
   * Check auth status - validates stored token and updates isAuthenticated
   *
   * @see Requirements 3.6, 4.3, 4.4
   */
  checkAuth: () => void;

  /**
   * Set loading state
   *
   * @param loading - Loading state value
   */
  setLoading: (loading: boolean) => void;

  /**
   * Set error state
   *
   * @param error - ApiError object or null to clear
   */
  setError: (error: ApiError | null) => void;

  /**
   * Clear error state
   *
   * @see Requirements 3.3
   */
  clearError: () => void;

  /**
   * Set hydration state (called by Zustand persist onRehydrateStorage)
   * @internal
   */
  setHasHydrated: (state: boolean) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  isAuthenticated: false,
  userId: null,
  email: null,
  sessionToken: null,
  expiresAt: null,
  isLoading: false,
  error: null,
  _hasHydrated: false,
};

// ============================================================================
// Store Creation
// ============================================================================

/**
 * Auth Store
 *
 * Zustand store for authentication state management with localStorage persistence.
 *
 * Features:
 * - Session token storage and validation
 * - Automatic token expiration handling
 * - Persistent state across browser sessions
 * - Error state management
 *
 * @example
 * ```tsx
 * // Check authentication status
 * const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
 *
 * // Login
 * const login = useAuthStore((state) => state.login);
 * login(sessionToken, expiresAt, userId, email);
 *
 * // Logout
 * const logout = useAuthStore((state) => state.logout);
 * await logout();
 *
 * // Check auth on app load
 * const checkAuth = useAuthStore((state) => state.checkAuth);
 * useEffect(() => { checkAuth(); }, [checkAuth]);
 * ```
 *
 * @see Requirements 3.1-3.7, 4.1-4.6
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ─────────────────────────────────────────────────────────────────────
      // Actions
      // ─────────────────────────────────────────────────────────────────────

      login: (sessionToken, expiresAt, userId, email) => {
        set({
          isAuthenticated: true,
          sessionToken,
          expiresAt,
          userId: userId ?? null,
          email: email ?? null,
          error: null,
          isLoading: false,
        });
      },

      logout: async () => {
        const { sessionToken } = get();

        // Clear state first (optimistic update)
        set({
          isAuthenticated: false,
          userId: null,
          email: null,
          sessionToken: null,
          expiresAt: null,
          error: null,
          isLoading: false,
        });

        // Call logout API (fire and forget)
        // Note: We don't wait for this or handle errors - state is already cleared
        if (sessionToken && API_BASE_URL) {
          try {
            await fetch(`${API_BASE_URL}/auth/logout`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${sessionToken}` },
            });
          } catch {
            // Ignore logout API errors - state is already cleared
            // This is intentional: user should be logged out locally regardless
          }
        }
      },

      checkAuth: () => {
        const { sessionToken, expiresAt, isAuthenticated } = get();

        // No token stored - not authenticated
        if (!sessionToken || !expiresAt) {
          // Only update if state actually changes
          if (isAuthenticated !== false) {
            set({ isAuthenticated: false });
          }
          return;
        }

        // Check if token is expired
        if (Date.now() > expiresAt) {
          // Token expired - clear all auth data (only if currently authenticated)
          if (isAuthenticated !== false) {
            set({
              isAuthenticated: false,
              sessionToken: null,
              expiresAt: null,
              userId: null,
              email: null,
            });
          }
          return;
        }

        // Token is valid - mark as authenticated (only if not already)
        if (isAuthenticated !== true) {
          set({ isAuthenticated: true });
        }
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist auth-related fields, not UI state like isLoading/error/_hasHydrated
      partialize: (state) => ({
        sessionToken: state.sessionToken,
        expiresAt: state.expiresAt,
        userId: state.userId,
        email: state.email,
      }),
      // Called when hydration is complete
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select isAuthenticated state
 *
 * @param state - Auth state
 * @returns Whether user is authenticated
 * @see Requirements 3.7
 */
export const selectIsAuthenticated = (state: AuthState): boolean => state.isAuthenticated;

/**
 * Select session token
 *
 * @param state - Auth state
 * @returns Session token or null
 */
export const selectSessionToken = (state: AuthState): string | null => state.sessionToken;

/**
 * Select auth error
 *
 * @param state - Auth state
 * @returns ApiError or null
 */
export const selectAuthError = (state: AuthState): ApiError | null => state.error;

/**
 * Select user info
 *
 * @param state - Auth state
 * @returns User ID and email
 */
export const selectUserInfo = (
  state: AuthState
): { userId: string | null; email: string | null } => ({
  userId: state.userId,
  email: state.email,
});

/**
 * Select loading state
 *
 * @param state - Auth state
 * @returns Loading state
 */
export const selectIsLoading = (state: AuthState): boolean => state.isLoading;

/**
 * Select hydration state
 *
 * @param state - Auth state
 * @returns Whether Zustand persist has completed hydration
 */
export const selectHasHydrated = (state: AuthState): boolean => state._hasHydrated;
